import { formatCurrency } from '@saaga/shared/utils/billing.util'
import { Loader2Icon, PlusIcon } from 'lucide-react'
import { memo, type ReactNode, useMemo } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

import type { AddSeatsConfirmProps, BillingSeatInterval } from './billing-seat.interface'

const formatIntervalLabel = (interval: BillingSeatInterval): string =>
  interval === 'month' ? 'month' : 'year'

interface PriceLineProps {
  label: string
  value: number
  isQuantity?: boolean
  className?: string
}

const PriceLine: React.FC<PriceLineProps> = ({
  label,
  value,
  isQuantity = false,
  className,
}): ReactNode => (
  <div className={cn('flex justify-between text-sm', className)}>
    <span className="text-muted-foreground">{label}</span>
    <span>{isQuantity ? `×${value}` : formatCurrency(value)}</span>
  </div>
)

interface PriceSummaryProps {
  quantity: number
  pricePerSeat: number
  intervalLabel: string
  isEarlyAccess: boolean
}

const PriceSummary: React.FC<PriceSummaryProps> = ({
  quantity,
  pricePerSeat,
  intervalLabel,
  isEarlyAccess,
}): ReactNode => {
  const totalPrice = quantity * pricePerSeat
  return (
    <div className="billing-add-seats-confirm-summary space-y-2 rounded-md bg-muted p-4">
      {!isEarlyAccess && (
        <PriceLine label={`Price per seat (per ${intervalLabel})`} value={pricePerSeat} />
      )}
      <PriceLine label="Quantity" value={quantity} isQuantity />
      {!isEarlyAccess && (
        <div className="border-t pt-2">
          <PriceLine
            label={`Total (per ${intervalLabel})`}
            value={totalPrice}
            className="font-semibold"
          />
        </div>
      )}
    </div>
  )
}

interface ConfirmNoteProps {
  isEarlyAccess: boolean
}

const ConfirmNote: React.FC<ConfirmNoteProps> = ({ isEarlyAccess }): ReactNode => (
  <p className="billing-add-seats-confirm-note text-sm text-muted-foreground">
    {isEarlyAccess
      ? 'These seats are included in your Early Access. No charges apply.'
      : 'You will be charged immediately for the prorated amount for this billing period.'}
  </p>
)

interface ConfirmFooterProps {
  isLoading: boolean
  seatLabel: string
  isEarlyAccess: boolean
  onConfirm: () => void
}

const ConfirmFooter: React.FC<ConfirmFooterProps> = ({
  isLoading,
  seatLabel,
  isEarlyAccess,
  onConfirm,
}): ReactNode => (
  <AlertDialogFooter>
    <AlertDialogCancel disabled={isLoading} className="billing-add-seats-confirm-cancel">
      Cancel
    </AlertDialogCancel>
    <AlertDialogAction
      onClick={onConfirm}
      disabled={isLoading}
      className="billing-add-seats-confirm-action"
    >
      {isLoading && <Loader2Icon className="mr-2 size-4 animate-spin" />}
      {isEarlyAccess ? `Add ${seatLabel}` : 'Confirm Purchase'}
    </AlertDialogAction>
  </AlertDialogFooter>
)

const BillingAddSeatsConfirmBase: React.FC<AddSeatsConfirmProps> = ({
  isOpen,
  onOpenChange,
  quantity,
  pricePerSeat,
  billingInterval,
  isLoading = false,
  onConfirm,
  isEarlyAccess = false,
  className,
}): ReactNode => {
  const intervalLabel = useMemo(() => formatIntervalLabel(billingInterval), [billingInterval])
  const seatLabel = quantity === 1 ? 'seat' : 'seats'
  const description = isEarlyAccess
    ? `You are about to add ${quantity} additional ${seatLabel}.`
    : `You are about to add ${quantity} additional ${seatLabel} to your subscription.`

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn('billing-add-seats-confirm', className)}>
        <AlertDialogHeader>
          <AlertDialogTitle className="billing-add-seats-confirm-title flex items-center gap-2">
            <PlusIcon className="size-5" />
            Add {quantity} {seatLabel}
          </AlertDialogTitle>
          <AlertDialogDescription className="billing-add-seats-confirm-description">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <PriceSummary
          quantity={quantity}
          pricePerSeat={pricePerSeat}
          intervalLabel={intervalLabel}
          isEarlyAccess={isEarlyAccess}
        />
        <ConfirmNote isEarlyAccess={isEarlyAccess} />
        <ConfirmFooter
          isLoading={isLoading}
          seatLabel={seatLabel}
          isEarlyAccess={isEarlyAccess}
          onConfirm={onConfirm}
        />
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const BillingAddSeatsConfirm = memo(BillingAddSeatsConfirmBase)

BillingAddSeatsConfirm.displayName = 'BillingAddSeatsConfirm'
